package network

import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.launch

/**
 * Modo prueba (sandbox) de Stripe: cobra contra las llaves de test, con tarjetas 4242…,
 * SIN mover dinero real. Pensado para probar el flujo completo una vez que la app ya está
 * en LIVE con clientes reales.
 *
 * Este flag es solo la petición del cliente: viaja como `modoPrueba` en el body del pago.
 * Quien decide de verdad es el servidor (`permiteModoPrueba` en lib/seguridad.js), que solo
 * lo acepta para rol master/desarrollador/admin_general. Un cliente normal no puede activarlo
 * aunque manipule la app: el servidor lo ignora y cobra en live.
 *
 * Se persiste en `usuarios/{uid}/modoPruebaPagos` para aguantar recargas de página: al hacer
 * varias pruebas seguidas no hay que volver a prenderlo cada vez. Solo se apaga cuando el
 * usuario lo apaga. El aviso naranja en Configuración de Pagos evita que quede encendido por
 * olvido.
 */
object ModoPruebaPagos {
    private val _activo = MutableStateFlow(false)
    val activo: StateFlow<Boolean> = _activo.asStateFlow()

    private var uidActual: String = ""
    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)

    /** Lee el estado guardado del usuario. Se llama al resolver su rol tras iniciar sesión. */
    suspend fun cargarDeUsuario(uid: String) {
        uidActual = uid
        if (uid.isBlank()) { _activo.value = false; return }
        _activo.value = try {
            val snap = FirebaseClient.database.reference("usuarios/$uid/modoPruebaPagos").valueEvents.first()
            if (snap.exists) snap.value<Boolean>() else false
        } catch (e: Exception) { false }
    }

    /** Apaga el flag en memoria al cerrar sesión (no toca lo guardado en el servidor). */
    fun limpiarSesion() {
        uidActual = ""
        _activo.value = false
    }

    fun activar(valor: Boolean) {
        _activo.value = valor
        val uid = uidActual
        if (uid.isBlank()) return
        // La UI no espera a la escritura: el flag ya cambió en memoria y el pago lo lee de ahí.
        scope.launch {
            try {
                FirebaseClient.database.reference("usuarios/$uid/modoPruebaPagos").setValue(valor)
            } catch (e: Exception) {
                // Si falla la escritura, el flag sigue activo en memoria para esta sesión.
            }
        }
    }
}
